
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ArtistsHeader from './components/ArtistsHeader';
import FilterSidebar from './components/FilterSidebar';
import FilterDrawer from './components/FilterDrawer';
import FeaturedCarousel from './components/FeaturedCarousel';
import ArtistCard from './components/ArtistCard';
import StickyPreviewBar from './components/StickyPreviewBar';
import EmptyState from './components/EmptyState';
import { Header } from '../../components/feature/Header';
import { LoadingGraphic } from '../../components/utility/LoadingGraphic';
import { type ArtistListItem } from '../../lib/artistsApi';
import { useArtistsBootstrap } from './useArtistsBootstrap';
import { getImageVariant } from '../../lib/media';

interface Artist {
  id: string;
  slug: string;
  name: string;
  avatar: string;
  city: string;
  lanes: string[];
  verified: boolean;
  likes: number;
  releasesCount: number;
  featuresCount: number;
  lastRelease: string;
  hasNewRelease: boolean;
  hasMemberships: boolean;
  roles: string[];
  highlightTrack?: {
    title: string;
    url: string;
    duration: number;
  };
}

const PLACEHOLDER_AVATAR = '/images/254artistdefault.png';

const parseLanes = (lanes: string | null): string[] => {
  if (!lanes) return [];
  try {
    const parsed = JSON.parse(lanes);
    return Array.isArray(parsed)
      ? parsed.filter((lane): lane is string => typeof lane === 'string')
      : [];
  } catch {
    return [];
  }
};

const mapArtist = (artist: ArtistListItem): Artist => ({
  id: String(artist.id),
  slug: artist.slug,
  name: artist.name,
  avatar: getImageVariant(artist.avatar_url, 'small') || artist.avatar_url || PLACEHOLDER_AVATAR,
  city: artist.city || artist.country || 'Kenya',
  lanes: parseLanes(artist.lanes),
  verified: Boolean(artist.is_verified),
  likes: artist.likes_count ?? 0,
  releasesCount: artist.releases_count ?? 0,
  featuresCount: artist.features_count ?? 0,
  lastRelease: 'Coming soon',
  hasNewRelease: false,
  hasMemberships: false,
  roles: [],
});

export default function ArtistsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [artists, setArtists] = useState<Artist[]>([]);
  const [featuredArtists, setFeaturedArtists] = useState<Artist[]>([]);
  const [filteredArtists, setFilteredArtists] = useState<Artist[]>([]);
  const [laneOptions, setLaneOptions] = useState<string[]>([]);
  const { data, loading: isLoading, error } = useArtistsBootstrap();
  const [currentPreview, setCurrentPreview] = useState<{
    artist: Artist;
    track: Artist['highlightTrack'];
    isPlaying: boolean;
    currentTime: number;
  } | null>(null);

  // Get current filters from URL
  const currentFilters = {
    q: searchParams.get('q') || '',
    lane: searchParams.get('lane') || '',
    city: searchParams.get('city') || '',
    verified: searchParams.get('verified') === 'true',
    hasNewRelease: searchParams.get('hasNewRelease') === 'true',
    hasMemberships: searchParams.get('hasMemberships') === 'true',
    roles: searchParams.get('roles') || '',
    sort: searchParams.get('sort') || 'trending'
  };

  useEffect(() => {
    if (!data) return;
    setArtists(data.artists.map(mapArtist));
    setFeaturedArtists(data.featured_artists.map(mapArtist));
    setLaneOptions(Array.isArray(data.lanes) ? data.lanes : []);
  }, [data]);

  // Apply filters
  useEffect(() => {
    let filtered = [...artists];

    // Search filter
    if (currentFilters.q) {
      const query = currentFilters.q.toLowerCase();
      filtered = filtered.filter(artist => {
        const normalizedCity = artist.city?.toLowerCase() ?? '';
        return (
          artist.name.toLowerCase().includes(query) ||
          normalizedCity.includes(query) ||
          artist.lanes.some(lane => lane.toLowerCase().includes(query))
        );
      });
    }

    // Lane filter
    if (currentFilters.lane) {
      filtered = filtered.filter(artist => 
        artist.lanes.includes(currentFilters.lane)
      );
    }

    // City filter
    if (currentFilters.city) {
      filtered = filtered.filter(artist => artist.city === currentFilters.city);
    }

    // Status filters
    if (currentFilters.verified) {
      filtered = filtered.filter(artist => artist.verified);
    }

    if (currentFilters.hasNewRelease) {
      filtered = filtered.filter(artist => artist.hasNewRelease);
    }

    if (currentFilters.hasMemberships) {
      filtered = filtered.filter(artist => artist.hasMemberships);
    }

    // Roles filter
    if (currentFilters.roles) {
      filtered = filtered.filter(artist => 
        artist.roles.includes(currentFilters.roles)
      );
    }

    // Sort
    switch (currentFilters.sort) {
      case 'followers':
        filtered.sort((a, b) => b.likes - a.likes);
        break;
      case 'newest': {
        const getTime = (value: string) => {
          const timestamp = Date.parse(value);
          return Number.isNaN(timestamp) ? 0 : timestamp;
        };
        filtered.sort((a, b) => getTime(b.lastRelease) - getTime(a.lastRelease));
        break;
      }
      case 'az':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'za':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default: // trending
        filtered.sort((a, b) => {
          const aScore = (a.verified ? 1000 : 0) + (a.hasNewRelease ? 500 : 0) + a.likes;
          const bScore = (b.verified ? 1000 : 0) + (b.hasNewRelease ? 500 : 0) + b.likes;
          return bScore - aScore;
        });
    }

    setFilteredArtists(filtered);
  }, [artists, searchParams]);

  // Save filters to localStorage
  useEffect(() => {
    const filters = Object.fromEntries(searchParams.entries());
    localStorage.setItem('artistsFilters', JSON.stringify(filters));
  }, [searchParams]);

  // Load saved filters on mount
  useEffect(() => {
    const saved = localStorage.getItem('artistsFilters');
    if (saved) {
      try {
        const filters = JSON.parse(saved);
        const newParams = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value) newParams.set(key, value as string);
        });
        setSearchParams(newParams, { replace: true });
      } catch (e) {
        // Ignore invalid JSON
      }
    }
  }, []);

  const handleFilterChange = (key: string, value: string | boolean) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (value === '' || value === false) {
      newParams.delete(key);
    } else {
      newParams.set(key, value.toString());
    }
    
    setSearchParams(newParams);
  };

  const handleClearFilters = () => {
    setSearchParams({});
  };

  const handlePreview = (artist: Artist) => {
    if (!artist.highlightTrack) return;

    if (currentPreview?.artist.id === artist.id) {
      setCurrentPreview(prev => prev ? { ...prev, isPlaying: !prev.isPlaying } : null);
    } else {
      setCurrentPreview({
        artist,
        track: artist.highlightTrack,
        isPlaying: true,
        currentTime: 0
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <ArtistsHeader
          searchQuery={currentFilters.q}
          onSearchChange={(q) => handleFilterChange('q', q)}
          resultCount={filteredArtists.length}
          sortBy={currentFilters.sort}
          onSortChange={(sort) => handleFilterChange('sort', sort)}
          onOpenFilters={() => setIsFilterDrawerOpen(true)}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {error && (
            <div className="mb-8 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
          {/* Featured Artists Carousel */}
          <FeaturedCarousel artists={featuredArtists} />

          <div className="flex gap-8 mt-8">
            {/* Desktop Filter Sidebar */}
            <div className="hidden lg:block w-64 flex-shrink-0">
              <FilterSidebar
                filters={currentFilters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                lanes={laneOptions}
              />
            </div>

            {/* Results Grid */}
            <div className="flex-1">
              {isLoading ? (
                <div className="rounded-lg border border-gray-200 bg-white">
                  <LoadingGraphic label="Loading artists..." />
                </div>
              ) : filteredArtists.length === 0 ? (
                <EmptyState
                  query={currentFilters.q}
                  onClearFilters={handleClearFilters}
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredArtists.map((artist) => (
                    <ArtistCard
                      key={artist.id}
                      artist={artist}
                      isPlaying={currentPreview?.artist.id === artist.id && currentPreview.isPlaying}
                      onPreview={() => handlePreview(artist)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Filter Drawer */}
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        filters={currentFilters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        lanes={laneOptions}
      />

      {/* Sticky Preview Bar */}
      {currentPreview && (
        <StickyPreviewBar
          artist={currentPreview.artist}
          track={currentPreview.track!}
          isPlaying={currentPreview.isPlaying}
          currentTime={currentPreview.currentTime}
          onPlayPause={() => setCurrentPreview(prev => 
            prev ? { ...prev, isPlaying: !prev.isPlaying } : null
          )}
          onClose={() => setCurrentPreview(null)}
        />
      )}
    </div>
  );
}
